import { InboxControlsState, ThreadListData, ThreadListQueryParams } from "../../types/ruleBasedInbox";
import { ACTION_TYPE_NAME } from "../../components/dashboard/flowBuilder/actionTypeName";
import { primaryColor, tertiaryColor } from "../../utils/colors";
import IconsColor from "../../components/utility/iconsColor";
import React from "react";

/**
 * Constants for Rule-Based Inbox
 */

/** Limit for fetching chat messages per request */
export const CHAT_MESSAGES_LIMIT = 50;

// Color Constants (matching SCSS variables from colorCode.scss)
export const THREAD_LIST_COLORS = {
    FILTER_ICON: "#616161",
    DRAWER_BACKDROP: "rgba(0, 0, 0, 0.1)",
    DRAWER_BOX_SHADOW: "rgba(0, 0, 0, 0.15)",
    DRAWER_BORDER: "#e0e0e0",
    ARCHIVE_ICON: "#9e9e9e",
    CHANNEL_ICON: "#757575",
} as const;

// Default pagination settings
export const DEFAULT_PAGE_LIMIT = 50;
export const DEFAULT_START_FROM = 1;

// ============ Recent Chats Configuration ============
// Threads within this time window (in minutes) will be marked as "Recent"
// These threads won't be sorted by timestamp and will stay in their position
export const RECENT_CHAT_THRESHOLD_MINUTES = 15;
// Interval (in milliseconds) to check and update recent status of threads
export const RECENT_CHAT_CHECK_INTERVAL_MS = 60 * 1000; // 1 minute
// Label to show instead of timestamp for recent chats
export const RECENT_CHAT_LABEL = "Recent";

// Default thread list data state
export const DEFAULT_THREAD_LIST_DATA: ThreadListData = {
    threads: [],
    threadsFiltered: [],
    stats: {
        total_records: 0,
        start_from: DEFAULT_START_FROM,
        limit: DEFAULT_PAGE_LIMIT,
    },
    isLoading: false,
};

/** Filter keys that should be synced to URL */
export const FILTER_URL_KEYS = [
    "conversationType",
    "flagUnassigned",
    "flagAssignedToMe",
    "flagArchived",
    "flagUnarchived",
    "ticketStatusIds",
    "ticketCreatedByIds",
    "flagTicketUnassigned",
    "flagTicketAssignedToOther",
    "ticketAssignedTo",
    "tags",
    "lastActivity",
    "searchQuery",
    "ticketSearchQuery",
    "integratedChannelId"
] as const;

// Default inbox controls filter state
export const DEFAULT_INBOX_CONTROLS_STATE: InboxControlsState = {
    conversationType: "Active",
    flagUnassigned: false, // Checkbox: show unassigned conversations
    flagAssignedToMe: false, // Checkbox: show conversations assigned to me
    flagArchived: false, // By default, don't filter by archived
    flagUnarchived: false, // By default, don't filter by unarchived
    ticketStatusIds: [], // Selected ticket status type IDs
    ticketCreatedBy: [], // Selected Ticket Created by user's user_assignment_id
    ticketCreatedByType: 'All',
    flagTicketUnassigned: false, // Show Threads which have unassigned tickets
    flagTicketAssignedToOther: false, // Show Threads which have tickets assigned to other
    ticketAssignedTo: [], // Show ticket assigned to user's user_assignment_id
    tags: [], // Selected tag IDs
    channels: [],
    cswState: {
        open: true,
        expiringSoon: true,
        expired: true,
    },
    lastActivity: "all",
    searchQuery: "",
    ticketSearchQuery: "",
    integratedChannelId: [], // To Search the Integrated Channels
};

// Default API query params
export const DEFAULT_QUERY_PARAMS: ThreadListQueryParams = {
    start_from: DEFAULT_START_FROM,
    limit: DEFAULT_PAGE_LIMIT,
    flag_active: true,
};

// Last Activity Filter Options
export const LAST_ACTIVITY_OPTIONS = [
    { value: "all", label: "All time" },
    { value: "24h", label: "Last 24 hours" },
    { value: "3d", label: "Last 3 days" },
    { value: "7d", label: "Last 7 days" },
] as const;

// Thread Metadata Keys
export const METADATA_KEYS = {
    PHONE_NUMBER: "phone_number",
    NAME: "name",
    EMAIL: "email",
    CHATBOT_USER_COUNTRY: "chatbot_user_country",
    CHATBOT_USER_COUNTRY_CODE: 'chatbot_user_country_code',
    USER_NAME: 'username',
} as const;

// Ticket Status Types
export const TICKET_STATUS_TYPES = {
    NO_TICKET: "no_ticket",
    OPEN: "Open",
    RESOLVED: "Resolved",
} as const;

// CSW (Conversation Service Window) States
export const CSW_STATES = {
    OPEN: "open",
    EXPIRING_SOON: "expiring_soon",
    EXPIRED: "expired",
} as const;

// Time format patterns
export const TIME_FORMAT = {
    RELATIVE: "relative", // e.g., "2h", "3d", "Yesterday"
    FULL: "full", // e.g., "Dec 22, 2025 06:13 AM"
} as const;

export const AGENT_ACTIVITY_ACTION_TYPE = {
    ASSIGN: "manual_agent_assigned",
    JOIN: "manual_agent_joined",
    LEFT: "manual_agent_left"
} 

export const agentInfoActionTypes = [AGENT_ACTIVITY_ACTION_TYPE['ASSIGN'], AGENT_ACTIVITY_ACTION_TYPE['JOIN'], AGENT_ACTIVITY_ACTION_TYPE['LEFT']];

export const CHATBOT_ACTION_TYPE = {
    CLOSED: "chatbot_closed",
    DEPLOYED: "chatbot_deployed",
    REDEPLOYED: "redeploy_chatbot"
} 

export const USER_MESSAGE_ACTION_TYPES = {
    TEXT: "WA_DM_text_received",
    MEDIA: "WA_DM_media_received",
    AI_MESSAGE_EXECUTION: "ai_message_execution"
}

export const INSTAGRAM_ACTION_TYPES = {
    IG_DM_TEXT_RECEIVED: "IG_DM_text_received",
    SEND_IG_DM_TEXT: "send_IG_DM_text",
    IG_COMMENT_RECEIVED: "ig_comment_received",
    REPLY_DM_TO_IG_COMMENT: "reply_dm_to_ig_comment",
    REPLY_TO_IG_COMMENT: "reply_to_ig_comment"
} 

// Bot actions that are rendered as action cards (no message text), so they get no copy icon
export const NON_COPYABLE_BOT_ACTION_TYPES = [
    ACTION_TYPE_NAME["CUSTOM_API"],
    ACTION_TYPE_NAME["SAVE_DATA"],
    ACTION_TYPE_NAME["RESET_DATA"],
    ACTION_TYPE_NAME["VARIABLE_ASSIGNMENT"],
    ACTION_TYPE_NAME["AWAIT_WEBHOOK"],
    ACTION_TYPE_NAME["SAVE_CONTACT"],
    ACTION_TYPE_NAME["REMOVE_CONTACT"],
    ACTION_TYPE_NAME["TALK_TO_AGENT"],
    ACTION_TYPE_NAME["VOICE_CALL"],
    ACTION_TYPE_NAME["ASSIGN_TAG"],
    ACTION_TYPE_NAME["REMOVE_TAG"],
    ACTION_TYPE_NAME["RETRIEVE_CONTACT"],
].map((actionType) => actionType?.toLowerCase());

export const DEFAULT_ACTION_TYPES = [
    USER_MESSAGE_ACTION_TYPES["TEXT"],
    ACTION_TYPE_NAME["TEXT"],
    ACTION_TYPE_NAME["TEMPLATE"],
    ACTION_TYPE_NAME["SEND_MEDIA"],
    AGENT_ACTIVITY_ACTION_TYPE['ASSIGN'],
    AGENT_ACTIVITY_ACTION_TYPE['JOIN'],
    AGENT_ACTIVITY_ACTION_TYPE['LEFT'],
    ACTION_TYPE_NAME["CAROUSEL_TEMPLATE"],
    ACTION_TYPE_NAME["SEND_ORDER_DETAIL_TEMPLATE"],
    CHATBOT_ACTION_TYPE["CLOSED"],
    CHATBOT_ACTION_TYPE["DEPLOYED"],
    USER_MESSAGE_ACTION_TYPES["MEDIA"],
    ACTION_TYPE_NAME["TIMEOUT_TRIGGER_EXECUTION"],
    USER_MESSAGE_ACTION_TYPES["AI_MESSAGE_EXECUTION"],
    ACTION_TYPE_NAME["VOICE_CALL"],
    INSTAGRAM_ACTION_TYPES["IG_COMMENT_RECEIVED"],
    INSTAGRAM_ACTION_TYPES["IG_DM_TEXT_RECEIVED"],
    INSTAGRAM_ACTION_TYPES["REPLY_DM_TO_IG_COMMENT"],
    INSTAGRAM_ACTION_TYPES["REPLY_TO_IG_COMMENT"],
    INSTAGRAM_ACTION_TYPES["SEND_IG_DM_TEXT"],
    ACTION_TYPE_NAME["MISCELLANEOUS"],
    ACTION_TYPE_NAME["ARCHIVE_CHAT"]
].map((actionType) => actionType?.toLowerCase());

export const userMessagesActionTypes = [
    USER_MESSAGE_ACTION_TYPES["TEXT"], 
    USER_MESSAGE_ACTION_TYPES["MEDIA"],
    INSTAGRAM_ACTION_TYPES["IG_DM_TEXT_RECEIVED"]
].map((actionType) => actionType?.toLowerCase());

export const VOICE_CALL_STATUS = {
    COMPLETED: "COMPLETED",
    ACCEPTED: "ACCEPTED",
    CONNECTED: "CONNECTED",
    RINGING: "RINGING",
    REJECTED: "REJECTED",
    FAILED: "FAILED"
}

export const VOICE_CALL_STATUS_LABEL = {
    [VOICE_CALL_STATUS['COMPLETED']]: 'Completed',
    [VOICE_CALL_STATUS['ACCEPTED']]: 'In Call',
    [VOICE_CALL_STATUS['CONNECTED']]: 'Connected',
    [VOICE_CALL_STATUS['RINGING']]: 'Ringing...',
    [VOICE_CALL_STATUS['FAILED']]: 'Failed',
}

export const FLAG_NEW_KEY = "flag_new"; 

export const MANUAL_OVERRIDE_BTN_TEXT = {
    TAKEOVER: 'Takeover',
    JOIN: 'Join',
    LEAVE: 'Leave'
} 

export const TIMEOUT_TRIGGER_EXECUTION_STATUS = {
    EXECUTED: 'Executed'
} 

export const TICKET_FIELDS = {
    HEADING: 'Ticket Heading',
    DESCRIPTION: 'Ticket Description',
    COMMENT: 'Comment',
    NOTES: 'Notes'
} 

export const ticketFieldSequenceMap = {
    [TICKET_FIELDS['HEADING']]: 1,
    [TICKET_FIELDS['DESCRIPTION']]: 2,
    [TICKET_FIELDS['COMMENT']]: 3,
    [TICKET_FIELDS['NOTES']]: 4
}

export const THREAD_CHANNEL_NAMES = {
    WHATSAPP: 'WhatsApp',
    CHRONOX_AI_WEB_APP: 'chronoxAiWebApp', 
    CHRNOX_AI_WEB_APP: 'chrnoxAiWebApp',
    WEBSITE: 'website',
    INSTAGRAM: 'Instagram',
    FACEBOOK: 'Facebook'
}

// ============================================================================
// THREAD PUSH DECISION LOGIC
// Determines if a new thread from socket should be added to the thread list
// ============================================================================

/**
 * Valid message action types that warrant adding/updating a thread
 * Only these action types should trigger thread list updates
 */
export const VALID_MESSAGE_ACTION_TYPES = [
    // Outgoing text messages
    "send_wa_dm_text",
    "send_web_dm_text",
    "send_messenger_dm_text",
    "send_instagram_dm_text",
    // Outgoing media messages
    "send_wa_dm_media",
    "send_web_dm_media",
    "send_messenger_dm_media",
    "send_instagram_dm_media",
    // Outgoing template messages
    "send_wa_dm_template",
    "send_wa_dm_carousel_template",
    "send_order_details_template",
    // Incoming messages (user messages)
    "wa_dm_text_received",
    "wa_dm_media_received",
    "web_dm_text_received",
    "web_dm_media_received",
    "messenger_dm_text_received",
    "messenger_dm_media_received",
    "instagram_dm_text_received",
    "instagram_dm_media_received",
    "manual_agent_assigned",
    "Agent assigned",
    "manual_agent_joined",
    "Agent joined",
    "manual_agent_left",
    "Agent left",
    "ai_message_execution",
    INSTAGRAM_ACTION_TYPES['IG_COMMENT_RECEIVED']?.toLowerCase(),
    INSTAGRAM_ACTION_TYPES['IG_DM_TEXT_RECEIVED']?.toLowerCase(),
    INSTAGRAM_ACTION_TYPES['REPLY_DM_TO_IG_COMMENT']?.toLowerCase(),
    INSTAGRAM_ACTION_TYPES['REPLY_TO_IG_COMMENT']?.toLowerCase(),
    INSTAGRAM_ACTION_TYPES['SEND_IG_DM_TEXT']?.toLowerCase()
] as const;

/**
 * Time threshold for considering a thread as "active" (in hours)
 */
export const ACTIVE_THREAD_HOURS_THRESHOLD = 72;

/**
 * Ticket status name that qualifies a thread as "active"
 */
export const ACTIVE_TICKET_STATUS = "Open";

// ============================================================================
// MAIN FUNCTION: Get Thread Visibility Decision
// ============================================================================

export const actionTypesToIgnoreForThreadMetaDataUpdate = [ACTION_TYPE_NAME['SAVE_DATA'], "await_user_message", ACTION_TYPE_NAME['AWAIT_WEBHOOK'], CHATBOT_ACTION_TYPE['REDEPLOYED'], ACTION_TYPE_NAME['CUSTOM_API']]
export const actionTypesToIgnoreWhenLastMessageUpdation = [AGENT_ACTIVITY_ACTION_TYPE['ASSIGN'], AGENT_ACTIVITY_ACTION_TYPE['JOIN'], AGENT_ACTIVITY_ACTION_TYPE['LEFT']]; 

/** Mapping of feedback data keys to display labels */
export const FEEDBACK_KEY_LABELS: Record<string, string> = {
    chatbot_message_feedback_value: "Issue category",
    chatbot_message_feedback_issue_description: "Issue description",
    chatbot_message_feedback_correct_response: "Correct response",
    chatbot_message_feedback_correct_response_file: "File reference for correct response",
    created_by_username: "User name",
    created_by_user_email: "User email",
    feedback_option_name: "Issue category",
    feedback_provider: "Feedback Provider",
}; 

export const addNewPhoneNumberObj = {
    meta_integration_whatsapp_phone_number_id: -1,
    phone_number_id: '',
    verified_name: "WhatsApp Business Account",
    code_verification_status: '',
    display_phone_number: '',
    quality_rating: '',
    flag_enable_calling: false,
    meta_integration_phone_number_flag_registered: false,
    meta_integration_phone_number_flag_webhook: false,
    meta_integration_phone_number_meta_data: 'string',
    created_at_timestamp: '',
    integrated_channel_id: 0,
    channel_id: 1,
    channel_name: 'WhatsApp',
    updated_by_user_assignment_id: 0,
    updated_at_timestamp: '',
    chatbot_integration_assignment_data: [],
    contact_chatbot_assignment_data: [],
    flag_send_agent_activity_message: false,
    agent_activity_message: [],
    flag_disable_agent_on_user_inactivity: false,
    time_of_inactivity_in_ms: 0,
    flag_smb_phone_number: false,
    flag_agent_assignment_on_smb_message: false,
    meta_integration_whatsapp_id: '',
} 

export const interactiveMessageTypes = ['button', 'catalog_message', 'list', 'product', 'product_list', 'flow', 'cta_url'] 

// : chatThemeColors
export const defaultChatThemeColors = {
    header_bg_color: '#FFFFFF',
    header_font_color: primaryColor,
    chatbot_bg_color: "#FFFFFF",
    agent_chip_bg_color: '#f0f0f0',
    user_msg_bg_color: tertiaryColor,
    user_msg_bg_color_mobile: "#F4F4F4",
    user_msg_color: "black",
    system_msg_bg_color: "#F4F4F4",
    system_msg_bg_color_mobile: tertiaryColor,
    system_msg_color: "black",
    no_conversation_color: "#000000",
    schedule_send_icon_color: "#000",
    send_msg_bg_color: "#FFFFFF",
    send_msg_icon_color: primaryColor,
    send_msg_font_color: primaryColor,
} 

export const ATTACHMENT_IMAGE_LABEL_TEXT = "Image";
export const ATTACHMENT_VIDEO_LABEL_TEXT = "Video";
export const ATTACHMENT_DOCUMENT_LABEL_TEXT = "Document";
export const ATTACHMENT_TEMPLATE_LABEL_TEXT = "Template";

export const fileType = [
    {
        label: ATTACHMENT_IMAGE_LABEL_TEXT,
        icon: React.createElement(IconsColor, { iconName: "image", fillColor: "#0c836f" }),
        acceptedFileType: ".jpg, .png, .jpeg"
    },
    {
        label: ATTACHMENT_VIDEO_LABEL_TEXT,
        icon: React.createElement(IconsColor, { iconName: "videoFile", fillColor: "#0c836f" }),
        acceptedFileType: ".mp4, .3gp",
    },
    {
        label: ATTACHMENT_DOCUMENT_LABEL_TEXT,
        icon: React.createElement(IconsColor, { iconName: "documentFile", fillColor: "#0c836f" }),
        acceptedFileType: ".pdf",
    },
    {
        label: ATTACHMENT_TEMPLATE_LABEL_TEXT,
        icon: React.createElement(IconsColor, { iconName: "templateFile", fillColor: "#0c836f" }),
        acceptedFileType: "",
    }
] 

export const emptyCannedResVariable = {
    canned_response_variable_id: null,
    variable_key: "",
    variable_description: "",
    variable_sample_value: "",
}

export const emptycannedResponseMessage = {
    canned_response_message_id: null,
    content_type: {
        id: 0,
        name: "",
    },
    content_type_id: 0,
    message_content: "",
    message_url: "",
    message_sequence: 1,
    canned_response_variables: [],
}

export const emptyCannedResponse = {
    canned_response_template_id: null,
    template_title: "",
    canned_response_messages: [
        emptycannedResponseMessage
    ]
} 

export const TEMPLATE_META_DATA_OPTIONS = [
    {
        label: "Name",
        key: "name",
    },
    {
        label: "Phone Number",
        key: "phone_number",
    }
]

export const CANNED_RESPONSE_VARIABLES = [
    ...TEMPLATE_META_DATA_OPTIONS,
    {
        label: "Agent Name",
        key: "agent_name",
    }
] 

export const chatLogsDefaultValue = {
    activeChats: [],
    closedChats: [],
    activeChatsFiltered: [],
    closeChatsFiltered: [],
    pagination: {
        start_index: 0,
        end_index: 0,
        total_chats: 0,
        records_per_page: 0
    }
} 

export const getNewThreadTagObj = {
    ticket_tag_id: "-1",
    ticket_tag_name: "New Tag",
    ticket_tag_color: null
} 

export const WHATSAPP_TEMPLATE_TYPE = {
    CALL_PERMISSION_REQUEST: 'Call Permission Request'
}

export const CALL_PERMISSION_REQUEST_OPTIONS = ["Always allow calls", "Temporarily allow calls", "Not now"];

export const MISCELLANEOUS_TYPES = {
    ACTIVE_CONTACTS_LIMIT_HIT: 'active_contacts_limit_hit',
    VOICE_MINUTES_EXHAUSTED: 'voice_minutes_exhausted',
    VOICE_AI_CREDITS_EXHAUSTED: 'voice_ai_credits_exhausted',
    AI_CREDITS_EXHAUSTED: 'ai_credits_exhausted',
    WHATSAPP_CAMPAIGN_MESSAGES_LIMIT_HIT: 'whatsapp_campaign_messages_limit_hit',
    TEXT: 'text'
}

export const patternToRemoveSource = /【\d+†source】/g;